import { Injectable, Injector } from '@angular/core';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { LoggerService } from '@app/core/services/logger.service';
import { RawFileStoreService } from '@app/core/services/raw-file-store.service';
import * as fromRoot from '@app/reducers';
import { buildCloudfrontError } from '@app/shared/helpers/cloudfront';
import { buildUploadInput, parseUploaderError } from '@app/shared/helpers/uploader';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { SmashUploader, UploaderError, UploaderEvents, UploaderParameters, UploaderStatus } from '@smash-sdk/uploader';
import { CustomEventEmitter } from '@smash-sdk/uploader/dist/es/core/CustomEventEmitter';
import { UploadCompleteOutput } from '@smash-sdk/uploader/dist/es/interface/Output';
import { defer, forkJoin, of } from 'rxjs';
import { catchError, concatMap, map, take } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { EmailDelivery, LinkDelivery, TransferCommonParameters } from '@app/client/uploader/interfaces/transfer';
import { PrepareActions, WorkflowActions, UploadActions } from '@app/client/uploader/store/actions';
import { ResolveCustomizationService } from '@app/core/services/resolvers/resolve-customization';
import { ResolveExternalPromotionService } from '@app/core/services/resolvers/resolve-external-promotion';
import { Gnitekram } from '@smash-sdk/gnitekram';
import moment from 'moment';

@Injectable()
export class UploadEffects {
  smashUploader: SmashUploader;
  logger: LoggerService;
  listeners: CustomEventEmitter[] = [];

  constructor(
    private actions$: Actions,
    private store$: Store<fromRoot.State & fromUploader.State>,
    private rawFileStoreService: RawFileStoreService,
    private gtmService: GoogleTagManagerService,
    private readonly localStorageService: LocalStorageService,
    private resolveCustomizationService: ResolveCustomizationService,
    private resolveExternalPromotionService: ResolveExternalPromotionService,
    private injector: Injector,
  ) {
    this.logger = this.injector.get(LoggerService);
  }

  upload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.Upload),
      concatLatestFrom(() => [
        this.store$.select(fromRoot.getCurrentDomain),
        this.store$.select(fromRoot.getSelectedLanguage),
        this.store$.select(fromRoot.getCurrentRegion),
        this.store$.select(fromRoot.getToken),
        this.store$.select(fromUploader.getUploadFormMode),
        this.store$.select(fromUploader.getTransferCommonParameters),
        this.store$.select(fromUploader.getTransferLinkParameters),
        this.store$.select(fromUploader.getTransferEmailParameters),
        this.store$.select(fromUploader.getDropboxPreview),
      ]),
      concatMap(([_, domain, language, region, token, uploadFormMode, commonParameters, linkParameters, emailParameters, dropbox]) => {
        const files = this.rawFileStoreService.getFiles().map(file => ({ name: file.relativePath, file: file.file }));

        return forkJoin([this.resolveExternalPromotion(commonParameters), this.resolveCustomization(commonParameters)]).pipe(
          concatMap(([resolvedPromotion, resolvedCustomization]) => {
            commonParameters = {
              ...commonParameters,
              promotion: resolvedPromotion,
              customization: resolvedCustomization,
            };

            const uploadInput = buildUploadInput({ domain, language, commonParameters, linkParameters, emailParameters, uploadFormMode, dropbox, files });

            const uploadParameters: UploaderParameters = { region, token } as UploaderParameters;
            this.smashUploader = new SmashUploader(uploadParameters);

            this.setupUploaderListeners();
            this.saveDeliveryParameters(uploadInput.delivery as EmailDelivery | LinkDelivery);

            return defer(() => this.smashUploader.upload(uploadInput)).pipe(
              concatMap((output: UploadCompleteOutput) => {
                if (output.status !== 'Canceling') {
                  return of(UploadActions.UploadSuccess({ transfer: output.transfer }));
                }
                return of({ type: 'NO_ACTION' });
              }),
              catchError((error) => {
                const cloudfrontError = this.handleUploadError(error);
                return of(UploadActions.UploadFailure({ error: cloudfrontError }));
              }),
            );
          }),
        )
      }))
  );

  updateAndResumeUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.UpdateAndResumeUpload),
      concatLatestFrom(() => [
        this.store$.select(fromRoot.getCurrentDomain),
        this.store$.select(fromRoot.getSelectedLanguage),
        this.store$.select(fromUploader.getUploadFormMode),
        this.store$.select(fromUploader.getTransferCommonParameters),
        this.store$.select(fromUploader.getTransferLinkParameters),
        this.store$.select(fromUploader.getTransferEmailParameters),
        this.store$.select(fromUploader.getDropboxPreview),
      ]),
      concatMap(([_, domain, language, uploadFormMode, commonParameters, linkParameters, emailParameters, dropbox]) => {
        if (!this.smashUploader) {
          throw new Error('SmashUploader is not initialized');
        }
        const files = this.rawFileStoreService.getFiles().map(file => ({ name: file.relativePath, file: file.file }));

        return forkJoin([this.resolveExternalPromotion(commonParameters), this.resolveCustomization(commonParameters)]).pipe(
          concatMap(([resolvedPromotion, resolvedCustomization]) => {
            commonParameters = {
              ...commonParameters,
              promotion: resolvedPromotion,
              customization: resolvedCustomization,
            };

            const uploadInput = buildUploadInput({ domain, language, commonParameters, linkParameters, emailParameters, uploadFormMode, dropbox, files });
            this.saveDeliveryParameters(uploadInput.delivery as EmailDelivery | LinkDelivery);

            return defer(() => this.smashUploader.update(uploadInput)).pipe(
              concatMap(() => {
                return defer(() => this.smashUploader.resume()).pipe(
                  concatMap(({ transfer }) => {
                    if (transfer?.queue > 0) {
                      this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Queued }));
                      this.store$.dispatch(PrepareActions.DisplayModalSizePremium());
                    }
                    return of(UploadActions.UpdateAndResumeUploadSuccess());
                  }),
                );
              }),
              catchError((error) => {
                const cloudfrontError = this.handleUploadError(error);
                return of(UploadActions.UpdateAndResumeUploadFailure({ error: cloudfrontError }));
              }),
            );
          }));
      })
    )
  );

  cancelUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.CancelUpload),
      map(() => {
        this.store$.dispatch(WorkflowActions.updateStep({ step: 'PREPARE' }));
        this.listeners.map(listener => listener.off());
        if (this.smashUploader) {
          this.smashUploader.cancel();
          this.smashUploader.off();
          this.smashUploader = null;
        }
        return UploadActions.CancelUploadSuccess();
      })
    )
  );

  resetUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.ResetUpload),
      map(() => {
        this.store$.dispatch(WorkflowActions.updateStep({ step: 'LANDING' }));
        this.listeners.map(listener => listener.off());
        if (this.smashUploader) {
          this.smashUploader.cancel();
          this.smashUploader.off();
          this.smashUploader = null;
        }
        this.rawFileStoreService.reset();
        return UploadActions.ResetUploadSuccess();
      })
    )
  );

  checkFirstTimeSender$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UploadActions.CheckFirstTimeSender),
      concatLatestFrom(() => [
        this.store$.select(fromRoot.getActiveIdentity),
      ]),
      concatMap(([action, identity]) => {
        if (!identity?.username) {
          const marketingSdk = new Gnitekram();
          const params = { id: unescape(encodeURIComponent(action.username)), key: action.transferId };
          return defer(() => marketingSdk.getUsage(params)).pipe(
            map(({ usage }) => {
              const isWithinLast14Days = usage?.firstSendDate && moment(usage.firstSendDate).isAfter(moment().subtract(14, 'days'));
              if (usage?.firstSendDate && isWithinLast14Days) {
                this.gtmService.pushTag({ event: Constant.eventGTM.FIRST_TIME_SENDER });
              }
              return UploadActions.CheckFirstTimeSenderSuccess({ usage });
            }),
            catchError((error) => {
              return of(UploadActions.CheckFirstTimeSenderFailure({ error }));
            })
          );
        }
        return of({ type: 'NO_ACTION' });
      })
    )
  );

  private resolveExternalPromotion(commonParameters: TransferCommonParameters) {
    return this.resolveExternalPromotionService.resolve().pipe(
      take(1),
      map(({ selectedPromotion, promotions }) => {
        return commonParameters.promotion || { id: selectedPromotion?.id || promotions.find(p => p.id)?.id };
      })
    );
  }

  private resolveCustomization(commonParameters: TransferCommonParameters) {
    return this.resolveCustomizationService.resolve().pipe(
      take(1),
      map(({ customization }) => {
        return commonParameters.customization || { background: customization?.background.sourceUrl, logo: customization?.logo.sourceUrl };
      })
    );
  }

  private setupUploaderListeners() {
    const startingListener = this.smashUploader.on(UploaderEvents.Starting, () => {
      this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Starting }));
    });

    const startedListener = this.smashUploader.on(UploaderEvents.Started, (data) => {
      this.gtmService.pushTag({ event: Constant.eventGTM.TRANSFER_INITIATED, transfer_id: data.data.transfer.id, });
      this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Started }));
    });

    const queuedListener = this.smashUploader.on(UploaderEvents.Queued, (event) => {
      this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Queued }));
      this.store$.dispatch(UploadActions.UploadQueued({ queue: event.data.transfer.queue, queuedUntil: event.data.transfer.queuedUntil }));
      this.store$.dispatch(PrepareActions.DisplayModalSizePremium());
    });

    const queueFinishedListener = this.smashUploader.on(UploaderEvents.QueueFinished, () => {
      this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Resumable }));
    });

    const finishingListener = this.smashUploader.on(UploaderEvents.Finishing, () => {
      this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Finishing }));
    });

    const finishedListener = this.smashUploader.on(UploaderEvents.Finished, (data) => {
      this.gtmService.pushTag({ event: Constant.eventGTM.TRANSFER_LOCKED, transfer_id: data.data.transfer.id, });
      this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Finished }));
      this.store$.dispatch(UploadActions.CheckFirstTimeSender({ username: data.data.transfer.delivery.sender.email, transferId: data.data.transfer.id }));
    });

    const progressListener = this.smashUploader.on(UploaderEvents.Progress, (event) => {
      this.store$.dispatch(UploadActions.SetProgress({ progress: event.data.percent }));
      this.store$.dispatch(UploadActions.SetUploadedBytes({ uploadedBytes: event.data.uploadedBytes }));
      this.store$.dispatch(UploadActions.SetRemainingSize({ remainingSize: event.data.totalBytes - event.data.uploadedBytes }));
      if (event.data.speed > 0) {
        this.store$.dispatch(UploadActions.SetUploadSpeed({ speed: event.data.speed }));
      }
    });

    this.listeners = [queuedListener, queueFinishedListener, startingListener, startedListener, progressListener, finishingListener, finishedListener];
  }

  private handleUploadError(error: UploaderError) {
    const parsedError = parseUploaderError(error);
    const cloudfrontError = buildCloudfrontError<UploaderError>(parsedError);
    this.logger.error(cloudfrontError);
    this.store$.dispatch(UploadActions.SetUploadStatus({ status: UploaderStatus.Error }));
    return cloudfrontError;
  }

  private saveDeliveryParameters(deliveryParameters: EmailDelivery | LinkDelivery) {
    this.localStorageService.setItem('lastTransferParameters', btoa(unescape(encodeURIComponent(JSON.stringify(deliveryParameters)))));
  }

}
